/**
 * RecurrenceEditor — Modal for editing recurrence rules.
 *
 * Supports all recur_json types: daily, weekly, monthly_date,
 * ordinal_monthly, interval, multiple_days, one-time (null).
 */

import React, { useState, useEffect, useMemo, useCallback, useId } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Modal,
  ScrollView,
  useWindowDimensions,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme, ThemeColors } from '../../utils/theme';
import { FONT_SIZE, SPACING, RADIUS } from '../../utils/design-tokens';
import { useBackHandler } from '../../navigation/useBackHandler';

type RecurType = 'daily' | 'weekly' | 'monthly_date' | 'ordinal_monthly' | 'interval' | 'multiple_days' | 'yearly_date';

interface RecurJson {
  type: RecurType;
  hour?: number;
  minute?: number;
  weekday?: number;
  weekdays?: number[];
  day?: number;
  month?: number;     // for yearly_date
  ordinal?: number | 'last';
  interval?: number;
  unit?: string;
}

interface Props {
  visible: boolean;
  value: string | null;          // current recur_json string (or null)
  isRecurring: boolean;
  onConfirm: (recurJson: RecurJson | null, isRecurring: boolean) => void;
  onCancel: () => void;
}

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const WEEKDAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const ORDINAL_LABELS = [
  { value: 1, label: '1st' },
  { value: 2, label: '2nd' },
  { value: 3, label: '3rd' },
  { value: 4, label: '4th' },
  { value: 'last' as const, label: 'Last' },
];
const INTERVAL_UNITS = ['day', 'week', 'month', 'year'];

const TYPE_OPTIONS: { type: RecurType; label: string; helper: string; icon: React.ComponentProps<typeof Ionicons>['name'] }[] = [
  { type: 'daily', label: 'Daily', helper: 'Every day', icon: 'sunny-outline' },
  { type: 'weekly', label: 'Weekly', helper: 'One weekday', icon: 'calendar-outline' },
  { type: 'multiple_days', label: 'Specific days', helper: 'Several weekdays', icon: 'grid-outline' },
  { type: 'monthly_date', label: 'Monthly date', helper: 'Same date monthly', icon: 'today-outline' },
  { type: 'ordinal_monthly', label: 'Monthly pattern', helper: 'First, second, or last weekday', icon: 'layers-outline' },
  { type: 'yearly_date', label: 'Yearly', helper: 'Same month and day', icon: 'star-outline' },
  { type: 'interval', label: 'Custom interval', helper: 'Every N days, weeks, months', icon: 'repeat-outline' },
];

function parseRecurJson(value: string | null): RecurJson | null {
  if (!value) return null;
  try {
    return typeof value === 'string' ? JSON.parse(value) : value;
  } catch {
    return null;
  }
}

function weekdayName(day: number) {
  return WEEKDAY_NAMES[Math.max(0, Math.min(6, day - 1))];
}

function monthName(month: number) {
  return MONTH_NAMES[Math.max(0, Math.min(11, month - 1))];
}

function ordinalLabel(value: number | 'last') {
  return ORDINAL_LABELS.find((option) => option.value === value)?.label || '1st';
}

function pluralize(value: number, unit: string) {
  return value === 1 ? unit : `${unit}s`;
}

export default function RecurrenceEditor({ visible, value, isRecurring, onConfirm, onCancel }: Props) {
  const { colors } = useTheme();
  const s = useMemo(() => createStyles(colors), [colors]);
  const { width } = useWindowDimensions();
  const modalId = useId();
  const modalW = Math.min(width - 32, 380);

  const [recurring, setRecurring] = useState(isRecurring);
  const [recurType, setRecurType] = useState<RecurType>('daily');
  const [weekday, setWeekday] = useState(1);        // 1=Mon
  const [weekdays, setWeekdays] = useState<number[]>([1]);
  const [monthDay, setMonthDay] = useState(1);
  const [ordinal, setOrdinal] = useState<number | 'last'>(1);
  const [intervalVal, setIntervalVal] = useState(2);
  const [intervalUnit, setIntervalUnit] = useState('day');
  const [yearlyMonth, setYearlyMonth] = useState(1);  // 1=Jan
  const [yearlyDay, setYearlyDay] = useState(1);

  const resetFromProps = useCallback(() => {
    setRecurring(isRecurring);
    const parsed = parseRecurJson(value);
    setRecurType(parsed?.type || 'daily');
    setWeekday(parsed?.weekday || 1);
    setWeekdays(parsed?.weekdays?.length ? parsed.weekdays : [1]);
    setMonthDay(parsed && parsed.type !== 'yearly_date' && parsed.day ? parsed.day : 1);
    setOrdinal(parsed?.ordinal !== undefined ? parsed.ordinal : 1);
    setIntervalVal(parsed?.interval || 2);
    setIntervalUnit(parsed?.unit || 'day');
    setYearlyMonth(parsed?.type === 'yearly_date' && parsed.month ? parsed.month : 1);
    setYearlyDay(parsed?.type === 'yearly_date' && parsed.day ? parsed.day : 1);
  }, [isRecurring, value]);

  // Sync from prop when opening
  useEffect(() => {
    if (visible) resetFromProps();
  }, [resetFromProps, visible]);

  const handleCancel = () => {
    onCancel();
  };

  useBackHandler({
    id: `recurrence-editor-${modalId}`,
    label: 'Recurrence editor',
    priority: 950,
    enabled: visible,
    onBack: () => {
      handleCancel();
      return true;
    },
  });

  const handleConfirm = () => {
    if (!recurring) {
      onConfirm(null, false);
      return;
    }
    const base: RecurJson = { type: recurType };
    switch (recurType) {
      case 'daily':
        break;
      case 'weekly':
        base.weekday = weekday;
        break;
      case 'multiple_days':
        base.weekdays = weekdays.length ? weekdays : [1];
        break;
      case 'monthly_date':
        base.day = monthDay;
        break;
      case 'ordinal_monthly':
        base.ordinal = ordinal;
        base.weekday = weekday;
        break;
      case 'interval':
        base.interval = intervalVal;
        base.unit = intervalUnit;
        break;
      case 'yearly_date':
        base.month = yearlyMonth;
        base.day = yearlyDay;
        break;
    }
    onConfirm(base, true);
  };

  const toggleWeekday = (d: number) => {
    setWeekdays((prev) =>
      prev.includes(d) ? (prev.length > 1 ? prev.filter((x) => x !== d) : prev) : [...prev, d].sort()
    );
  };

  const selectedSummary = useMemo(() => {
    if (!recurring) return 'Runs one time only';
    switch (recurType) {
      case 'daily':
        return 'Every day';
      case 'weekly':
        return `Every ${weekdayName(weekday)}`;
      case 'multiple_days': {
        const labels = weekdays.map(weekdayName).join(', ');
        return `Every ${labels}`;
      }
      case 'monthly_date':
        return `Every month on day ${monthDay}`;
      case 'ordinal_monthly':
        return `Every month on the ${ordinalLabel(ordinal).toLowerCase()} ${weekdayName(weekday)}`;
      case 'yearly_date':
        return `Every ${monthName(yearlyMonth)} ${yearlyDay}`;
      case 'interval':
        return `Every ${intervalVal} ${pluralize(intervalVal, intervalUnit)}`;
      default:
        return 'Repeats';
    }
  }, [intervalUnit, intervalVal, monthDay, ordinal, recurring, recurType, weekday, weekdays, yearlyDay, yearlyMonth]);

  const renderWeekdayPicker = (mode: 'single' | 'multi') => (
    <View style={s.controlBlock}>
      <Text style={s.controlLabel}>{mode === 'single' ? 'Repeat day' : 'Repeat on'}</Text>
      <View style={s.pillGrid}>
        {WEEKDAY_LABELS.map((label, index) => {
          const day = index + 1;
          const selected = mode === 'single' ? weekday === day : weekdays.includes(day);
          return (
            <TouchableOpacity
              key={label}
              style={[s.pillButton, selected && s.pillButtonActive]}
              onPress={() => (mode === 'single' ? setWeekday(day) : toggleWeekday(day))}
              accessibilityRole="button"
              accessibilityLabel={`${selected ? 'Selected' : 'Select'} ${weekdayName(day)}`}
            >
              <Text style={[s.pillText, selected && s.pillTextActive]}>{label}</Text>
            </TouchableOpacity>
          );
        })}
      </View>
    </View>
  );

  const renderNumberControl = (label: string, value: number, onDecrease: () => void, onIncrease: () => void) => (
    <View style={s.controlBlock}>
      <Text style={s.controlLabel}>{label}</Text>
      <View style={s.stepperRow}>
        <TouchableOpacity style={s.stepperButton} onPress={onDecrease} accessibilityRole="button" accessibilityLabel={`Decrease ${label}`}>
          <Ionicons name="remove" size={18} color={colors.primary} />
        </TouchableOpacity>
        <Text style={s.stepperValue}>{value}</Text>
        <TouchableOpacity style={s.stepperButton} onPress={onIncrease} accessibilityRole="button" accessibilityLabel={`Increase ${label}`}>
          <Ionicons name="add" size={18} color={colors.primary} />
        </TouchableOpacity>
      </View>
    </View>
  );

  const renderTypeControls = () => {
    switch (recurType) {
      case 'daily':
        return <Text style={s.activeNote}>Repeats every day at this reminder's time.</Text>;
      case 'weekly':
        return renderWeekdayPicker('single');
      case 'multiple_days':
        return renderWeekdayPicker('multi');
      case 'monthly_date':
        return renderNumberControl(
          'Day of month',
          monthDay,
          () => setMonthDay((value) => Math.max(1, value - 1)),
          () => setMonthDay((value) => Math.min(31, value + 1)),
        );
      case 'ordinal_monthly':
        return (
          <>
            <View style={s.controlBlock}>
              <Text style={s.controlLabel}>Occurrence</Text>
              <View style={s.pillGrid}>
                {ORDINAL_LABELS.map((option) => {
                  const selected = ordinal === option.value;
                  return (
                    <TouchableOpacity
                      key={String(option.value)}
                      style={[s.pillButton, selected && s.pillButtonActive]}
                      onPress={() => setOrdinal(option.value)}
                      accessibilityRole="button"
                      accessibilityLabel={`${selected ? 'Selected' : 'Select'} ${option.label}`}
                    >
                      <Text style={[s.pillText, selected && s.pillTextActive]}>{option.label}</Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
            </View>
            {renderWeekdayPicker('single')}
          </>
        );
      case 'yearly_date':
        return (
          <>
            <View style={s.controlBlock}>
              <Text style={s.controlLabel}>Month</Text>
              <View style={s.pillGrid}>
                {MONTH_LABELS.map((label, index) => {
                  const month = index + 1;
                  const selected = yearlyMonth === month;
                  return (
                    <TouchableOpacity
                      key={label}
                      style={[s.pillButton, selected && s.pillButtonActive]}
                      onPress={() => setYearlyMonth(month)}
                      accessibilityRole="button"
                      accessibilityLabel={`${selected ? 'Selected' : 'Select'} ${monthName(month)}`}
                    >
                      <Text style={[s.pillText, selected && s.pillTextActive]}>{label}</Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
            </View>
            {renderNumberControl(
              'Day of month',
              yearlyDay,
              () => setYearlyDay((value) => Math.max(1, value - 1)),
              () => setYearlyDay((value) => Math.min(31, value + 1)),
            )}
          </>
        );
      case 'interval':
        return (
          <>
            {renderNumberControl(
              'Repeat every',
              intervalVal,
              () => setIntervalVal((value) => Math.max(1, value - 1)),
              () => setIntervalVal((value) => Math.min(365, value + 1)),
            )}
            <View style={s.controlBlock}>
              <Text style={s.controlLabel}>Unit</Text>
              <View style={s.pillGrid}>
                {INTERVAL_UNITS.map((unit) => {
                  const selected = intervalUnit === unit;
                  return (
                    <TouchableOpacity
                      key={unit}
                      style={[s.pillButton, selected && s.pillButtonActive]}
                      onPress={() => setIntervalUnit(unit)}
                      accessibilityRole="button"
                      accessibilityLabel={`${selected ? 'Selected' : 'Select'} ${unit}`}
                    >
                      <Text style={[s.pillText, selected && s.pillTextActive]}>{pluralize(2, unit)}</Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
            </View>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={handleCancel}>
      <View style={s.overlay}>
        <View style={[s.modal, { width: modalW }]}>
          <View style={s.header}>
            <Text style={s.title}>Recurrence</Text>
            <Text style={s.summaryText}>{selectedSummary}</Text>
          </View>

          <View style={s.modeTabs}>
            <TouchableOpacity
              style={[s.modeTab, !recurring && s.modeTabActive]}
              onPress={() => setRecurring(false)}
              accessibilityRole="button"
              accessibilityLabel="Set one-time reminder"
            >
              <Ionicons name="radio-button-off-outline" size={16} color={!recurring ? colors.primary : colors.textSecondary} />
              <Text style={[s.modeTabText, !recurring && s.modeTabTextActive]}>One-time</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[s.modeTab, recurring && s.modeTabActive]}
              onPress={() => setRecurring(true)}
              accessibilityRole="button"
              accessibilityLabel="Set repeating reminder"
            >
              <Ionicons name="repeat-outline" size={16} color={recurring ? colors.primary : colors.textSecondary} />
              <Text style={[s.modeTabText, recurring && s.modeTabTextActive]}>Repeats</Text>
            </TouchableOpacity>
          </View>

          <View style={s.previewCard}>
            <View style={s.previewIcon}>
              <Ionicons name={recurring ? 'repeat-outline' : 'radio-button-off-outline'} size={17} color={colors.primary} />
            </View>
            <View style={s.previewCopy}>
              <Text style={s.previewLabel}>{recurring ? 'Current repeat' : 'Schedule'}</Text>
              <Text style={s.previewValue}>{selectedSummary}</Text>
            </View>
          </View>

          {recurring && (
            <ScrollView style={s.scroll} showsVerticalScrollIndicator={false}>
              <Text style={s.sectionLabel}>Frequency</Text>
              <View style={s.optionList}>
                {TYPE_OPTIONS.map((option) => {
                  const selected = recurType === option.type;
                  return (
                    <View key={option.type} style={[s.optionCard, selected && s.optionCardActive]}>
                      <TouchableOpacity
                        style={s.optionHeader}
                        onPress={() => setRecurType(option.type)}
                        accessibilityRole="button"
                        accessibilityLabel={`Set recurrence to ${option.label}`}
                      >
                        <View style={[s.optionIcon, selected && s.optionIconActive]}>
                          <Ionicons name={option.icon} size={16} color={selected ? colors.primary : colors.textSecondary} />
                        </View>
                        <View style={s.optionCopy}>
                          <Text style={[s.optionTitle, selected && s.optionTitleActive]}>{option.label}</Text>
                          <Text style={s.optionHelper}>{option.helper}</Text>
                        </View>
                        {selected ? <Ionicons name="checkmark-circle" size={18} color={colors.primary} /> : null}
                      </TouchableOpacity>
                      {selected ? <View style={s.selectedControls}>{renderTypeControls()}</View> : null}
                    </View>
                  );
                })}
              </View>
            </ScrollView>
          )}

          <View style={s.footer}>
            <TouchableOpacity onPress={handleCancel} style={s.cancelBtn} accessibilityRole="button" accessibilityLabel="Cancel recurrence changes">
              <Text style={s.cancelText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={handleConfirm} style={s.confirmBtn} accessibilityRole="button" accessibilityLabel="Save recurrence changes">
              <Text style={s.confirmText}>Save</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: colors.overlay,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: SPACING.base,
  },
  modal: {
    backgroundColor: colors.card,
    borderRadius: RADIUS.xl,
    padding: SPACING.base,
    maxHeight: '88%',
  },
  header: {
    alignItems: 'center',
    marginBottom: SPACING.md,
  },
  title: {
    fontSize: FONT_SIZE.md,
    fontWeight: '700',
    color: colors.text,
    textAlign: 'center',
  },
  summaryText: {
    marginTop: 4,
    fontSize: FONT_SIZE.sm,
    fontWeight: '600',
    color: colors.textSecondary,
    textAlign: 'center',
  },
  modeTabs: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.sm,
    padding: 4,
    borderRadius: RADIUS.md,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.surfaceTinted,
  },
  modeTab: {
    flex: 1,
    minHeight: 40,
    borderRadius: RADIUS.sm,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
  modeTabActive: {
    backgroundColor: colors.card,
    borderWidth: 1,
    borderColor: colors.primary,
  },
  modeTabText: {
    fontSize: FONT_SIZE.sm,
    fontWeight: '700',
    color: colors.textSecondary,
  },
  modeTabTextActive: {
    color: colors.primary,
  },
  previewCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.md,
    marginTop: SPACING.md,
    padding: SPACING.md,
    borderRadius: RADIUS.md,
    borderWidth: 1,
    borderColor: colors.borderBold,
    backgroundColor: colors.primaryLight,
  },
  previewIcon: {
    width: 34,
    height: 34,
    borderRadius: 17,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.card,
  },
  previewCopy: {
    flex: 1,
    minWidth: 0,
  },
  previewLabel: {
    fontSize: FONT_SIZE.xs,
    fontWeight: '700',
    color: colors.textSecondary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  previewValue: {
    marginTop: 2,
    fontSize: FONT_SIZE.base,
    fontWeight: '700',
    color: colors.primary,
  },
  scroll: {
    maxHeight: 390,
    marginTop: SPACING.md,
  },
  sectionLabel: {
    fontSize: FONT_SIZE.xs,
    fontWeight: '700',
    color: colors.textSecondary,
    textTransform: 'uppercase',
    letterSpacing: 0,
    marginBottom: SPACING.sm,
  },
  optionList: {
    gap: SPACING.sm,
    paddingBottom: SPACING.sm,
  },
  optionCard: {
    borderRadius: RADIUS.sm,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.card,
    overflow: 'hidden',
  },
  optionCardActive: {
    borderColor: colors.primary,
    backgroundColor: colors.primaryLight,
  },
  optionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.sm,
    paddingHorizontal: SPACING.md,
    paddingVertical: SPACING.md,
    minHeight: 58,
  },
  optionIcon: {
    width: 30,
    height: 30,
    borderRadius: 15,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surfaceTinted,
  },
  optionIconActive: {
    backgroundColor: colors.card,
  },
  optionCopy: {
    flex: 1,
    minWidth: 0,
  },
  optionTitle: {
    fontSize: FONT_SIZE.sm,
    fontWeight: '700',
    color: colors.text,
  },
  optionTitleActive: {
    color: colors.primary,
  },
  optionHelper: {
    marginTop: 2,
    fontSize: FONT_SIZE.xs,
    fontWeight: '600',
    color: colors.textSecondary,
  },
  selectedControls: {
    paddingHorizontal: SPACING.md,
    paddingBottom: SPACING.md,
    gap: SPACING.sm,
  },
  activeNote: {
    fontSize: FONT_SIZE.sm,
    fontWeight: '600',
    color: colors.textSecondary,
    paddingTop: 2,
  },
  controlBlock: {
    gap: SPACING.sm,
  },
  controlLabel: {
    fontSize: FONT_SIZE.xs,
    fontWeight: '700',
    color: colors.textSecondary,
    textTransform: 'uppercase',
    letterSpacing: 0,
  },
  pillGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  pillButton: {
    minWidth: 42,
    minHeight: 34,
    paddingHorizontal: SPACING.sm,
    paddingVertical: 7,
    borderRadius: RADIUS.sm,
    borderWidth: 1,
    borderColor: colors.border,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.card,
  },
  pillButtonActive: {
    borderColor: colors.primary,
    backgroundColor: colors.primary,
  },
  pillText: {
    fontSize: FONT_SIZE.xs,
    fontWeight: '700',
    color: colors.text,
  },
  pillTextActive: {
    color: '#FFFFFF',
  },
  stepperRow: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: RADIUS.md,
    overflow: 'hidden',
    backgroundColor: colors.card,
  },
  stepperButton: {
    width: 40,
    height: 38,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surfaceTinted,
  },
  stepperValue: {
    minWidth: 52,
    textAlign: 'center',
    fontSize: FONT_SIZE.lg,
    fontWeight: '700',
    color: colors.primary,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: SPACING.sm,
    marginTop: SPACING.md,
    paddingTop: SPACING.md,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  cancelBtn: {
    paddingVertical: SPACING.md,
    paddingHorizontal: SPACING.base,
    borderRadius: RADIUS.sm,
    minWidth: 96,
    alignItems: 'center',
  },
  cancelText: {
    fontSize: FONT_SIZE.base,
    fontWeight: '600',
    color: colors.textSecondary,
  },
  confirmBtn: {
    paddingVertical: SPACING.md,
    paddingHorizontal: SPACING.lg,
    borderRadius: RADIUS.sm,
    backgroundColor: colors.primary,
    minWidth: 112,
    alignItems: 'center',
  },
  confirmText: {
    fontSize: FONT_SIZE.base,
    fontWeight: '600',
    color: '#FFFFFF',
  },
});
